<template>
  <div class="flex flex-col">
    <div class="flex items-center justify-between mb-4">
      <slot />
    </div>

    <div
      class="flex-1 rounded-lg border bg-card text-card-foreground shadow-2xs"
      v-loading="loading"
    >
      <div class="p-6 border-b">
        <h3 class="text-2xl font-semibold leading-none tracking-tight">
          Snooze Passcode
        </h3>
        <p class="mt-2 text-sm text-muted-foreground">
          This passcode is shared by all WhatsApp alert rules in this project.
        </p>
      </div>

      <div class="p-6 max-w-2xl">
        <el-form label-position="top" @submit.native.prevent>
          <el-form-item
            label="Passcode"
            :error="passcodeError"
          >
            <el-input
              v-model="passcode"
              placeholder="Enter at least 4 letters or numbers"
              maxlength="64"
              show-password
              clearable
              @input="validatePasscode"
            >
              <template #append>
                <el-tooltip content="Copy passcode" placement="top">
                  <el-button
                    icon="el-icon-document-copy"
                    :disabled="!isPasscodeValid"
                    @click="copyPasscode"
                  />
                </el-tooltip>
              </template>
            </el-input>
          </el-form-item>

          <el-alert
            :title="
              enabled
                ? 'Passcode protection is enabled.'
                : 'Passcode protection is disabled.'
            "
            :type="enabled ? 'warning' : 'info'"
            :closable="false"
            show-icon
            class="mb-5"
          />

          <div class="flex items-center gap-3">
            <el-button icon="el-icon-refresh" @click="generatePasscode">
              Generate New Passcode
            </el-button>
            <el-button
              type="primary"
              :loading="saving"
              :disabled="!isPasscodeValid || !isDirty"
              @click="savePasscode"
            >
              Save / Reset
            </el-button>
            <el-button
              v-if="enabled"
              type="danger"
              plain
              :loading="deleting"
              @click="deletePasscode"
            >
              Disable Passcode
            </el-button>
          </div>

        </el-form>
      </div>
    </div>
  </div>
</template>

<script>
import copy from "copy-to-clipboard";
import {
  deleteNotificationSnoozePasscode,
  getNotificationSnoozePasscode,
  updateNotificationSnoozePasscode,
} from "@/api";

export default {
  name: "PasscodeSettings",
  data() {
    return {
      passcode: "",
      originalPasscode: "",
      passcodeError: "",
      enabled: false,
      loading: false,
      saving: false,
      deleting: false,
    };
  },
  computed: {
    isPasscodeValid() {
      return /^[A-Za-z0-9]{4,}$/.test(this.passcode);
    },
    isDirty() {
      return this.passcode !== this.originalPasscode;
    },
  },
  created() {
    this.fetchPasscode();
  },
  methods: {
    normalizePasscode(data) {
      if (typeof data === "string") return data;
      return (data && (data.passcode || data.access_code)) || "";
    },
    normalizeEnabled(data) {
      return !!(data && data.enabled);
    },
    async fetchPasscode() {
      this.loading = true;
      try {
        const data = await getNotificationSnoozePasscode();
        this.passcode = this.normalizePasscode(data);
        this.originalPasscode = this.passcode;
        this.enabled = this.normalizeEnabled(data);
      } catch (error) {
        this.$message.error(this.errorMessage(error, "Failed to load passcode."));
      } finally {
        this.loading = false;
      }
    },
    generatePasscode() {
      const alphabet =
        "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
      const random = new Uint32Array(6);
      window.crypto.getRandomValues(random);
      this.passcode = Array.from(
        random,
        (value) => alphabet[value % alphabet.length]
      ).join("");
      this.validatePasscode();
    },
    validatePasscode() {
      if (!this.passcode) {
        this.passcodeError = "Passcode is required.";
      } else if (!/^[A-Za-z0-9]+$/.test(this.passcode)) {
        this.passcodeError = "Only letters and numbers are allowed.";
      } else if (this.passcode.length < 4) {
        this.passcodeError = "Passcode must be at least 4 characters.";
      } else {
        this.passcodeError = "";
      }
    },
    copyPasscode() {
      if (this.passcode && copy(this.passcode)) {
        this.$message.success("Passcode copied to clipboard.");
      } else {
        this.$message.error("Failed to copy passcode.");
      }
    },
    async savePasscode() {
      this.validatePasscode();
      if (!this.isPasscodeValid) return;
      await this.$confirm(
        "The current passcode will stop working immediately. Continue?",
        "Reset Snooze Passcode",
        {
          confirmButtonText: "Reset Passcode",
          cancelButtonText: "Cancel",
          type: "warning",
        }
      );

      this.saving = true;
      try {
        const data = await updateNotificationSnoozePasscode({
          passcode: this.passcode,
        });
        this.passcode = this.normalizePasscode(data) || this.passcode;
        this.originalPasscode = this.passcode;
        this.enabled = true;
        this.$message.success("Snooze passcode updated.");
      } catch (error) {
        this.$message.error(this.errorMessage(error, "Failed to update passcode."));
      } finally {
        this.saving = false;
      }
    },
    async deletePasscode() {
      await this.$confirm(
        "Disabling passcode protection allows snooze changes without a passcode. Continue?",
        "Disable Snooze Passcode",
        {
          confirmButtonText: "Disable Passcode",
          cancelButtonText: "Cancel",
          type: "warning",
        }
      );

      this.deleting = true;
      try {
        await deleteNotificationSnoozePasscode();
        this.passcode = "";
        this.originalPasscode = "";
        this.passcodeError = "";
        this.enabled = false;
        this.$message.success("Snooze passcode disabled.");
      } catch (error) {
        this.$message.error(
          this.errorMessage(error, "Failed to disable passcode.")
        );
      } finally {
        this.deleting = false;
      }
    },
    errorMessage(error, fallback) {
      const status = error && error.response && error.response.status;
      if (status === 403) return "You do not have permission to manage passcodes.";
      if (status === 404) return "Passcode service is not available.";
      return (
        (error && error.response && error.response.data && error.response.data.error) ||
        (error && error.message) ||
        fallback
      );
    },
  },
};
</script>
